<template>
  <li
    ref="toastLi"
    class="w-full bg-black bg-opacity-80 text-white rounded-2xl backdrop-blur-sm shadow-2xl p-4 flex items-start gap-3 text-sm overflow-hidden border border-white/40"
    :aria-label="`${toast.actionIsMessage ? 'New Message' : 'Event or Upcoming Event'} – ${toast.title} – ${toast.actionIsMessage ? 'Message Content' : 'Details'}: ${toast.description}`"
  >
    <button
      v-if="toast.dismissible"
      type="button"
      aria-label="Dismiss Notification"
      class="absolute top-2 right-2 bg-white rounded-lg bg-opacity-10 hover:bg-opacity-20 p-1 text-white z-50"
      @click="(e) => interact(e, dismissToast)">
      <XMarkIcon
        aria-hidden="true"
        class="h-4 w-4"
      />
    </button>
    <div
      class="rounded-full h-6 w-6 flex items-center justify-center min-w-6 mt-2 overflow-hidden"
      :class="toast.avatar ? 'bg-white bg-opacity-10' : toast.iconClasses"
    >
      <img
        v-if="toast.avatar"
        :src="toast.avatar"
        :alt="toast.avatarAlt"
        class="h-full w-full object-cover"
      />
      <Component
        v-else-if="toast.icon"
        :is="toast.icon"
        :aria-label="toast.iconAria"
        class="h-4 w-4"
      />
    </div>
    <div class="flex-1 overflow-hidden max-w-full">
      <Component
        :is="titleHasOverflowX ? HoverTooltip : 'div'"
        placement="bottom"
        :tip="toast.title">
        <p
          ref="title"
          class="font-bold truncate pr-6">
          {{ toast.title }}
        </p>
      </Component>
      <p
        ref="description"
        class="text-xs opacity-60 font-medium mt-1 pr-6"
        :class="!showFullDescription && 'line-clamp-2 max-h-8'">
        {{ toast.description }}
      </p>
      <button
        v-if="descriptionHasOverflowY"
        type="button"
        class="text-xs underline pt-2 pb-1 opacity-70 hover:opacity-100 text-white transition-all block text-left"
        @click="(e) => interact(e, toggleShowFullDescription)">
        {{ showFullDescription ? "Show Less" : "Show More" }}
      </button>
      <form
        v-if="toast.actionIsMessage"
        class="relative mt-3"
        @submit.prevent="handleMessage"
      >
        <MessageTextarea
          v-model="message"
          :is-focused="isFocused"
          :placeholder="`Reply to ${toast.conversationSenderFirst || 'user'}...`"
          is-small-dark
          @update:height="setContentHeight"
          @update:is-focused="updateIsFocused"
          @keydown.enter.prevent="handleMessage"
        />
        <button
          type="submit"
          class="rounded-md p-1.5 bg-bdBlue-600 text-white flex items-center justify-center absolute right-1 top-1/2 -translate-y-1/2"
          aria-label="Send Message"
          :class="!message && 'opacity-80'"
        >
          <span
            v-if="messageLoading"
            class="spinning-loader"
            aria-label="Sending Message"
            style="--loader-diameter:16px;--loader-border-size:0.2rem;"
          />
          <PaperAirplaneIcon
            v-else
            aria-label="Paper Airplane Icon"
            class="h-4 w-4"
          />
        </button>
      </form>
      <div
        v-else-if="actionButtons.length"
        class="flex items-center mt-3 gap-3">

        <FormButton
          v-for="(actionButton, index) in actionButtons"
          :key="index"
          :use-router-link="actionButton.useRouterLink"
          :use-raw-link="actionButton.useRawLink"
          :to="actionButton?.to"
          :href="actionButton?.href"
          :color="actionButton.color"
          :class="actionButton.classes"
          size="sm"
          bold
          @click="(e) => interact(e, actionButton.onClick)">
          {{ actionButton.text }}
        </FormButton>

      </div>
    </div>
  </li>
</template>

<script setup>
import HoverTooltip from "./HoverTooltip.vue";
import MessageTextarea from "./MessageTextarea.vue";
import FormButton from "./FormButton.vue";
import MessageApi from "../api/MessageApi";
import {PaperAirplaneIcon, XMarkIcon} from "@heroicons/vue/24/solid/index.js";
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {serializeFormData} from "../helpers/CaseTransformer";
import {useAccountStore} from "../stores/AccountStore";
import {useRouter} from "vue-router";
import {useLogInAs} from "../composables/LogInAs";

const emits = defineEmits(["update:height", "dismiss", "update:is-focused", "before-unmount"]);
const props = defineProps({
  toast: {
    type: Object,
    required: true
  },
  isFocused: {
    type: Boolean,
    default: false,
  },
  getChannel: {
    type: Function,
    default: () => null,
  },
});

const accountStore = useAccountStore();
const router = useRouter();

const toastLi = ref();
const message = ref("");
const messageLoading = ref(false);
const title = ref();
const description = ref();

const titleHasOverflowX = ref(false);
const descriptionHasOverflowY = ref(true);
const showFullDescription = ref(false);

const {
  loginAsCompany
} = useLogInAs(router);

const hasMessage = computed(() => !!message.value);

const handleMessage = async () => {
  if (!hasMessage.value) {
    toast.warning("Please enter a message to send");
    return;
  }

  if (!props.toast.conversationUuid || !props.toast.conversationMessageId) {
    toast.warning("There was an issue responding to this conversation, please contact support");
    return;
  }

  if (messageLoading.value) return;
  messageLoading.value = true;

  const formData = serializeFormData({
    conversation_uuid: props.toast.conversationUuid,
    reply_to_id: props.toast.conversationMessageId,
    type: "text",
    content_type: "text",
    content: message.value,
  }, "post");

  const api = new MessageApi();

  try {
    await api.createWithFiles(formData);
    message.value = "";
    dismissToast();
  } finally {
    messageLoading.value = false;
  }
};

const dismissToast = () => emits("dismiss");

const updateIsFocused = (value) => emits("update:is-focused", value);

const setContentHeight = () => {
  const height = toastLi.value.scrollHeight;
  emits("update:height", height);
};

const setOverflows = () => {
  titleHasOverflowX.value = title.value.scrollWidth > title.value.clientWidth;
  descriptionHasOverflowY.value = description.value.scrollHeight > description.value.clientHeight;
  showFullDescription.value = false;
};

const toggleShowFullDescription = async () => {
  showFullDescription.value = !showFullDescription.value;
  await nextTick();
  setContentHeight();
};

const actionButtons = computed(() => {
  const goToLinkFromButton = async (isAbsolute, isRelative, link, callback) => {
    const path = new URL(link, window.location.origin);
    const alreadyOnPage = path.pathname === window.location.pathname;

    if (isRelative && !alreadyOnPage) {
      const query = Object.fromEntries(path.searchParams.entries());
      await router.push({path: path.pathname, query});
    } else if (isAbsolute) window.location.href = link;

    if (callback) await callback();
  };

  const createButtons = (setCompanyId, text, color, link, callback, isPrimary = false) => {
    const isRelative = link && link[0] === "/";
    const isAbsolute = link && !isRelative;

    const relativeParams = {to: link};
    const absoluteParams = {href: link, target: "_blank"};

    return {
      text,
      color,
      useRouterLink: !setCompanyId && isRelative,
      useRawLink: !setCompanyId && isAbsolute,
      ...(link && !setCompanyId) ? (isRelative ? relativeParams : absoluteParams) : {},
      onClick: async (e) => {
        if (!setCompanyId || accountStore?.user?.activeCompany?.id === setCompanyId) {
          await goToLinkFromButton(isAbsolute, isRelative, link, callback);
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        toast.info("Switching to the right account...");

        const targetCompany = accountStore.user.companies.find(c => c.id === setCompanyId);

        if (!targetCompany) {
          return toast.error("Hmm, we couldn't find the company you are trying to switch to – please contact support");
        }

        await loginAsCompany(targetCompany.id, targetCompany.billableEntityId, async () => {
          await accountStore.fetchUserAccount();
        });

        await nextTick();

        if (setCompanyId !== accountStore.user.activeCompany.id) {
          return toast.error("There was an issue logging into the company you are trying to switch to – please contact support");
        }

        await goToLinkFromButton(isAbsolute, isRelative, link, callback);
      },
      classes: isPrimary ? "w-full flex-1" : "max-w-fit",
    };
  };

  const t = props.toast;

  const buttons = [
    createButtons(t.setCompanyId, t.primaryActionText, t.primaryActionButtonColor, t.primaryActionLink, t.primaryActionCallback, true),
    createButtons(t.setCompanyId, t.secondaryActionText, t.secondaryActionButtonColor, t.secondaryActionLink, t.secondaryActionCallback)
  ];

  return buttons.filter(b => b.text);
});

let isTypingInterval;

const whisperIsTyping = () => {
  if (!hasMessage.value || !props.toast.actionIsMessage) return;

  const channel = props.getChannel(true);
  if (channel) channel.whisper("start-typing", {id: accountStore.user.id});
};

const whisperStopTyping = () => {
  if (!props.toast.actionIsMessage) return;

  const channel = props.getChannel();
  if (channel) channel.whisper("stop-typing", {id: accountStore.user.id});
};

watch(() => hasMessage.value, (hasMessage, hadMessage) => {
  if (hasMessage === hadMessage) return;
  clearInterval(isTypingInterval);

  if (hasMessage) {
    whisperIsTyping();
    isTypingInterval = setInterval(whisperIsTyping, 2000);
    return;
  }

  whisperStopTyping();
});

const interact = (e, callback) => {
  if (typeof props.toast?.onInteract === "function") props.toast.onInteract();
  callback(e);
};

onMounted(() => {
  setContentHeight();
  setOverflows();
});

onBeforeUnmount(() => {
  clearInterval(isTypingInterval);
  whisperStopTyping();
  emits("before-unmount");
});
</script>
